import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { initials } from "@/lib/format";
import { Mail } from "lucide-react";
import TwoFactorSection from "@/components/TwoFactorSection";

export default function Settings() {
  const { user, refresh, loading } = useAuth();
  const nav = useNavigate();
  const [avatar, setAvatar] = useState("");
  const [bio, setBio] = useState("");
  const [signature, setSignature] = useState("");
  const [pmEnabled, setPmEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (user) {
      setAvatar(user.avatar_url || "");
      setBio(user.bio || "");
      setSignature(user.signature || "");
      setPmEnabled(user.pm_enabled !== false);
    }
  }, [user, loading, nav]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", { avatar_url: avatar, bio, signature, pm_enabled: pmEnabled });
      await refresh();
      toast.success("محفوظ ہو گیا");
    } catch {
      toast.error("محفوظ نہیں ہو سکا");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="settings-page">
      <h1 className="urdu-naskh text-3xl font-semibold text-primary border-b-2 border-primary pb-2">{t.settings}</h1>
      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={avatar || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary font-eng">{initials(user.username)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-1.5">
            <Label className="urdu-naskh">{t.avatar}</Label>
            <Input value={avatar} onChange={(e) => setAvatar(e.target.value)} dir="ltr" placeholder="https://…" data-testid="settings-avatar" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="urdu-naskh">{t.bio}</Label>
          <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="اپنے بارے میں کچھ لکھیے…" className="urdu-naskh" data-testid="settings-bio" />
        </div>
        <div className="space-y-1.5">
          <Label className="urdu-naskh">{t.signature}</Label>
          <Textarea value={signature} onChange={(e) => setSignature(e.target.value)} rows={2} placeholder="آپ کا دستخط جو ہر پیغام کے نیچے دکھایا جائے گا…" className="urdu-naskh" data-testid="settings-signature" />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h2 className="urdu-naskh text-xl font-semibold text-primary flex items-center gap-2">
          <Mail size={18} /> رازداری
        </h2>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="urdu-naskh font-semibold">نجی پیغامات موصول کریں</div>
            <div className="urdu-naskh text-sm text-muted-foreground">
              اگر بند کریں گے تو دوسرے اراکین آپ کو نجی پیغام نہیں بھیج سکیں گے۔
            </div>
          </div>
          <Switch checked={pmEnabled} onCheckedChange={setPmEnabled} data-testid="settings-pm-toggle" />
        </div>
      </Card>

      <TwoFactorSection />

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-primary" data-testid="settings-save">
          {saving ? t.loading : t.saveProfile}
        </Button>
      </div>
    </div>
  );
}
